import { differenceInDays, formatDistanceToNowStrict, isPast, isToday, isTomorrow, isYesterday } from 'date-fns';
import React from 'react';
import { telemetry } from '../../../@vendor/Posthog';
import { TaskData } from '../../../api/endpoints/tasks';
import { Colors } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { DateBadgeButton } from '../../../ui/DateBadgeButton';
import { Copy, Flexer } from '../elements/LayoutElements';
import { toTaskDueDate, useOnPatchTask } from './TaskActions';

export const formatTaskDueDate = (dueDate: Date | string) => {
    const date = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;

    if (!date) return 'no due date';
    if (isToday(date)) return 'due today';
    if (isTomorrow(date)) return 'due tomorrow';
    if (isYesterday(date)) return 'due yesterday';

    const past = isPast(date);
    const distance = formatDistanceToNowStrict(date, {
        unit: 'day',
        roundingMethod: past ? 'floor' : 'ceil',
    });

    if (past) {
        return `due ${distance} ago`;
    }
    else {
        return `due in ${distance}`;
    }
};

export const computeTaskDueDateColor = (dueDate: Date | string) => {
    const date = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
    if (isToday(date)) return Colors.observe;
    if (isPast(date)) return Colors.negative;
    if (differenceInDays(date, new Date()) < 6) return Colors.observe;
    return Colors.gray100;
};

export const TaskDueDateCell = React.memo(({ readonly, task }: { readonly?: boolean, task: TaskData }) => {
    const { dueDate, status } = (task);
    if (status === 'closed') return null;

    const color = computeTaskDueDateColor(dueDate);
    const onPatchTask = useOnPatchTask(task.id);

    const onChange = (next: Date) => {
        onPatchTask({ dueDate: toTaskDueDate(next) });
        telemetry.emit('tasksView.taskTable.setDueDate', { taskId: task.id });
    };

    if (readonly) {
        return <Badge size="md" color={color}>
            {formatTaskDueDate(dueDate)}
        </Badge>;
    }

    if (status === 'done') {
        return <Flexer padding="0 0.375">
            <Copy size="0.8125" bold flush strike color={Colors.textLightest}>
                {formatTaskDueDate(dueDate)}
            </Copy>
        </Flexer>;
    }

    return (
        <DateBadgeButton
            color={color}
            value={typeof dueDate === 'string' ? new Date(dueDate) : dueDate}
            placeholder="no due date"
            formatter={formatTaskDueDate}
            onChange={onChange}
        />
    );
});
